import type { IncidentStatus } from "./api";
import type { IncidentListSearch } from "./search";
import { STATUS_ORDER } from "./status";

/**
 * The list's Status filter as a set (CD-012, LP-035): several Stati at once
 * combine with ODER inside the field, still UND with every other filter. The
 * API has taken a comma-joined `statuses` since the dashboard drilldowns
 * (F-020); this is only the control for it.
 */

function isStatus(value: string): value is IncidentStatus {
  return (STATUS_ORDER as readonly string[]).includes(value);
}

/** Always in lifecycle order, so equal selections compare equal. */
function ordered(statuses: Iterable<IncidentStatus>): IncidentStatus[] {
  const set = new Set(statuses);
  return STATUS_ORDER.filter((status) => set.has(status));
}

/** Seed from a deep link: `statuses` wins over the single `status`. */
export function seedStatuses(search: Pick<IncidentListSearch, "status" | "statuses">): IncidentStatus[] {
  if (search.statuses) return ordered(search.statuses.split(",").filter(isStatus));
  return search.status ? [search.status] : [];
}

export function toggleStatus(
  selected: readonly IncidentStatus[],
  status: IncidentStatus,
): IncidentStatus[] {
  return selected.includes(status)
    ? selected.filter((s) => s !== status)
    : ordered([...selected, status]);
}

/** The wire value, or undefined for "no Status filter". */
export function statusesParam(selected: readonly IncidentStatus[]): string | undefined {
  return selected.length > 0 ? ordered(selected).join(",") : undefined;
}

export function sameSelection(a: readonly IncidentStatus[], b: readonly IncidentStatus[]): boolean {
  return statusesParam(a) === statusesParam(b);
}
